import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { formatImpQtyWithTracking } from "../epsfLedgerUi";

export function txnKitNumberList(row, field = "KitNumbers") {
  const raw =
    field === "ExpectedKitNumbers"
      ? row?.ExpectedKitNumbers || row?.expectedKitNumbers
      : field === "PhysicalKitNumbers"
        ? row?.PhysicalKitNumbers || row?.physicalKitNumbers
        : field === "DiscrepancyKitDetails"
          ? row?.DiscrepancyKitDetails || row?.discrepancyKitDetails
          : row?.KitNumbers ||
            row?.kitNumbers ||
            row?.KitNumberList ||
            row?.kitNumberList ||
            "";
  if (Array.isArray(raw)) {
    return raw.map((x) => formatKitListItem(x)).filter(Boolean);
  }
  return String(raw || "")
    .split(/[,;\n]+/)
    .map((x) => formatKitListItem(x))
    .filter(Boolean);
}

/** "KIT-1|MISSING" → "KIT-1 · Missing"; plain kit numbers stay as-is. */
function formatKitListItem(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";
  const pipe = text.indexOf("|");
  if (pipe <= 0) return text;
  const kit = text.slice(0, pipe).trim();
  const result = text.slice(pipe + 1).trim().toUpperCase();
  if (!kit) return "";
  if (result === "MISSING") return `${kit} · Missing`;
  if (result === "UNEXPECTED") return `${kit} · Unexpected`;
  if (result === "MATCHED") return `${kit} · Matched`;
  return result ? `${kit} · ${result}` : kit;
}

export function txnKitNumbers(row) {
  const list = txnKitNumberList(row);
  return list.length ? list.join(", ") : null;
}

/** Qty / label cell with optional kits floating popover — Returns, Destruction, Reconcile. */
export default function ImpQtyKitFloatCell({
  row,
  display = null,
  kits: kitsProp = null,
  kitField = "KitNumbers",
  floatTitle = null,
  /** When true, always show clickable control even if kit list is empty. */
  allowEmptyClick = false,
  emptyMessage = null,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const floatRef = useRef(null);
  const kits = useMemo(() => {
    if (Array.isArray(kitsProp)) {
      return kitsProp.map((x) => String(x || "").trim()).filter(Boolean);
    }
    return txnKitNumberList(row, kitField);
  }, [kitsProp, row, kitField]);
  const qtyLabel =
    display != null && display !== ""
      ? String(display)
      : formatImpQtyWithTracking(row, row.Quantity ?? row.quantity, {
          allowZero: false,
        });

  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 168;
    const left = Math.min(
      Math.max(8, r.left),
      window.innerWidth - width - 8,
    );
    let top = r.bottom + 6;
    const approxH = Math.min(16 + kits.length * 28, 220);
    if (top + approxH > window.innerHeight - 8) {
      top = Math.max(8, r.top - approxH - 6);
    }
    setPos({ top, left });
  }, [kits.length]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePos();
    const onWin = () => updatePos();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      const t = ev.target;
      if (btnRef.current?.contains(t) || floatRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!kits.length && !allowEmptyClick) {
    return qtyLabel;
  }

  const title = floatTitle || (kits.length ? `Kits (${kits.length})` : "Kits");

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={["report-table__cell-link", className].filter(Boolean).join(" ")}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Show kits"
        onClick={() => setOpen((v) => !v)}
      >
        {qtyLabel}
      </button>
      {open
        ? createPortal(
            <div
              ref={floatRef}
              className="epsf-return-qty__float"
              role="dialog"
              aria-label={title}
              style={{ top: pos.top, left: pos.left }}
            >
              <div className="epsf-return-qty__float-head">
                <span>{title}</span>
                <button
                  type="button"
                  className="epsf-return-qty__float-close"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                >
                  ×
                </button>
              </div>
              {kits.length ? (
                <ul className="epsf-return-qty__float-list">
                  {kits.map((kit) => (
                    <li key={kit}>{kit}</li>
                  ))}
                </ul>
              ) : (
                <p className="epsf-return-qty__float-empty">
                  {emptyMessage ||
                    (kitField === "DiscrepancyKitDetails"
                      ? "No kit discrepancy detail recorded."
                      : "No kits recorded.")}
                </p>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
